import React, { useMemo } from 'react';
import { Card, Col, Row, Flex } from 'antd';
import {
  ExclamationCircleFilled,
  WarningFilled,
  CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { CustomShipmentText, CustomTitle } from '@/components';
import { ListItem } from '../types';

export interface SummaryData {
  is_critical?: number | null;
  is_risk?: number | null;
  is_track?: number | null;
  version?: number;
  unchanged?: boolean;
}

interface SummaryCardsProps {
  isCalendarView?: boolean;
  summaryData: SummaryData;
  calendarSummaryOverride?: SummaryData | null;

  drawerSource?: string;
  isSupportCompany: boolean;
  panelApplyFilters:
    | null
    | ((args: {
        filterBy?: string;
        items?: ListItem[];
        append?: boolean;
        groupTitle?: string;
      }) => void);
  drawerOpen: boolean;
  tableView?: boolean;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
  summaryData,
  calendarSummaryOverride = null,
  isCalendarView,
  isSupportCompany,
  panelApplyFilters,
  drawerSource,
  drawerOpen,
  tableView = false,
}) => {
  const { t } = useTranslation('shipments');

  const effectiveSummary = useMemo(() => {
    const isCalendar = Boolean(isCalendarView);
    return isCalendar && calendarSummaryOverride
      ? calendarSummaryOverride
      : summaryData;
  }, [isCalendarView, calendarSummaryOverride, summaryData]);

  const cardConfigs = useMemo(
    () => [
      {
        id: '0',
        title: t('criticalAlerts'),
        count: effectiveSummary?.is_critical,
        bg: '#DE1135',
        icon: <ExclamationCircleFilled />,
        name: t('criticalAlert'),
      },
      {
        id: '1',
        title: t('atRisk'),
        count: effectiveSummary?.is_risk,
        bg: '#F6BC2F',
        icon: <WarningFilled />,
        name: t('atRisk'),
      },
      {
        id: '2',
        title: t('noIssue'),
        count: effectiveSummary?.is_track,
        bg: '#0E8345',
        icon: <CheckOutlined />,
        name: t('noIssue'),
      },
    ],
    [effectiveSummary, t]
  );

  const colSpans = drawerOpen
    ? {
        xxl: 8,
        xl: 8,
        lg: 16,
        md: 24,
        sm: tableView ? 8 : 24,
        xs: tableView ? 8 : 24,
      }
    : {
        xxl: 5,
        xl: 5,
        lg: 5,
        md: 12,
        sm: tableView ? 8 : 24,
        xs: tableView ? 8 : 24,
      };

  const handleCardClick = (cfg: { id: string; name: string }) => {
    if (isSupportCompany) return;
    if (!panelApplyFilters) return;

    panelApplyFilters({
      filterBy: 'type',
      items: [{ id: cfg.id, name: cfg.name } as ListItem],
      append: true,
      groupTitle: t('risk'),
    });
  };

  return (
    <Row
      gutter={{
        xs: 8,
        sm: 8,
        md: 24,
        lg: 24,
        xl: 24,
      }}
      className={`summary-row responsive-row ${drawerSource === 'calendar' && drawerOpen ? 'shipment-section open' : ''}`}
      style={{ marginBottom: '20px' }}
    >
      {cardConfigs.map((cfg) => (
        <Col
          key={cfg.id}
          {...colSpans}
          style={
            drawerSource !== 'calendar'
              ? { transition: 'all 0.3s ease' }
              : undefined
          }
        >
          <Card
            onClick={() => handleCardClick(cfg)}
            className='CT-summery-cards'
          >
            <Flex justify='space-between' align='center'>
              <div>
                <CustomTitle
                  text={cfg.count ?? 0}
                  color='var(--text-primary)'
                  fontSize='18px'
                  lineHeight='24px'
                  fontWeight='700'
                  marginBottom={0}
                />
                <CustomShipmentText
                  text={cfg.title}
                  lineHeight='20px'
                  className='toolbar-filterd-text'
                />
              </div>

              <Flex className={` ${tableView && 'sm-d-none'}`}>
                <Flex
                  align='center'
                  justify='center'
                  style={{
                    backgroundColor: cfg.bg,
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                  }}
                >
                  <span style={{ fontSize: '16px', color: 'white' }}>
                    {cfg.icon}
                  </span>
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default SummaryCards;
